/**
 * Bra size calculator (EU / UK) based on underbust + bust measurements.
 */
(() => {
  const BAND_RANGES = [
    [59, 66, [60, 28]],
    [66.1, 71, [65, 30]],
    [71.1, 76, [70, 32]],
    [76.1, 81, [75, 34]],
    [81.1, 86, [80, 36]],
    [86.1, 91, [85, 38]],
    [91.1, 96, [90, 40]],
    [96.1, 101, [95, 42]],
    [101.1, 106, [100, 44]],
    [106.1, 111, [105, 46]],
    [111.1, 116, [110, 48]],
    [116.1, 121, [115, 50]],
    [121.1, 126, [120, 52]],
    [126.1, 131, [125, 54]],
    [131.1, 136, [130, 56]],
  ];

  const CUP_RANGES = [
    [10, 12.5, ['A', 'A']],
    [12.6, 15, ['B', 'B']],
    [15.1, 17.5, ['C', 'C']],
    [17.6, 20, ['D', 'D']],
    [20.1, 22.5, ['E', 'DD']],
    [22.6, 25, ['F', 'E']],
    [25.1, 27.5, ['G', 'F']],
    [27.6, 30, ['H', 'FF']],
    [30.1, 32.5, ['I', 'G']],
    [32.6, 35, ['J', 'GG']],
    [35.1, 37.5, ['K', 'H']],
    [37.6, 40, ['L', 'HH']],
    [40.1, 42.5, ['M', 'J']],
    [42.6, 45, ['N', 'JJ']],
    [45.1, 47.5, ['O', 'K']],
  ];

  /** For EU 65, larger cups are shifted 2 sizes back (Alia fit). K–O stay as calculated. */
  const BAND_65_EU_CUP_MAP = {
    A: 'A',
    B: 'A',
    C: 'A',
    D: 'B',
    E: 'C',
    F: 'D',
    G: 'E',
    H: 'F',
    I: 'G',
    J: 'H',
  };

  const EU_TO_UK_CUP = Object.fromEntries(CUP_RANGES.map(([, , cups]) => [cups[0], cups[1]]));

  const METHOD_WARNINGS = {
    soft: '',
    pushup:
      'W przypadku pomiaru w biustonoszu typu push-up (z dodatkowym wypełnieniem) podany rozmiar może być zawyżony. Jeśli szukasz miękkiego lub lekko usztywnianego stanika, przymierz biustonosze mniejsze o jeden rozmiar miseczki.',
    nobra:
      'W przypadku pomiaru bez biustonosza podany rozmiar może być zaniżony. Dokonaj pomiaru w miękkim staniku bez dodatkowych wypełnień.',
  };

  /**
   * @param {number} value
   * @param {Array<[number, number, any]>} ranges
   */
  function findRange(value, ranges) {
    return ranges.find(([min, max]) => value >= min && value <= max) || null;
  }

  /**
   * @param {number} underbust
   * @param {number} bust
   */
  function calculateSize(underbust, bust) {
    if (underbust < 59) {
      return { error: 'too_small' };
    }

    const bandMatch = findRange(underbust, BAND_RANGES);
    if (!bandMatch) {
      return { error: 'unsupported' };
    }

    const [euBand, ukBand] = bandMatch[2];
    const diff = Number((bust - euBand).toFixed(1));

    if (diff < 10 || diff > 47.5) {
      return { error: 'unsupported' };
    }

    const cupMatch = findRange(diff, CUP_RANGES);
    if (!cupMatch) {
      return { error: 'unsupported' };
    }

    let [euCup, ukCup] = cupMatch[2];

    if (euBand === 65 && BAND_65_EU_CUP_MAP[euCup]) {
      euCup = BAND_65_EU_CUP_MAP[euCup];
      ukCup = EU_TO_UK_CUP[euCup] || ukCup;
    }

    return {
      eu: `${euBand}${euCup}`,
      uk: `${ukBand}${ukCup}`,
      euBand,
      euCup,
    };
  }

  /**
   * @param {HTMLElement} root
   */
  function initCalculator(root) {
    if (!(root instanceof HTMLElement) || root.dataset.braCalcReady === 'true') return;
    root.dataset.braCalcReady = 'true';

    const form = root.querySelector('[data-bra-calc-form]');
    const submitBtn = root.querySelector('[data-bra-calc-submit]');
    const errorEl = root.querySelector('[data-bra-calc-error]');
    const resultEl = root.querySelector('[data-bra-calc-result]');
    const sizeEl = root.querySelector('[data-bra-calc-size]');
    const warningEl = root.querySelector('[data-bra-calc-warning]');
    const applyBtn = root.querySelector('[data-bra-calc-apply]');
    const underbustInput = root.querySelector('[data-bra-calc-underbust]');
    const bustInput = root.querySelector('[data-bra-calc-bust]');
    const selectSelector = root.dataset.braSelect || '#bra-size-select';

    if (!(form instanceof HTMLElement) || !(submitBtn instanceof HTMLButtonElement)) return;

    // Keep calculator fields out of the product form submission/validation.
    form.querySelectorAll('input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.setAttribute('form', `bra-calc-detached-${root.id || 'default'}`);
      }
    });

    /** @type {{ eu: string, uk: string } | null} */
    let lastResult = null;

    const sanitizeInput = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      target.value = target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
    };

    underbustInput?.addEventListener('input', sanitizeInput);
    bustInput?.addEventListener('input', sanitizeInput);

    const hideResult = () => {
      if (errorEl instanceof HTMLElement) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      if (resultEl instanceof HTMLElement) resultEl.hidden = true;
      if (warningEl instanceof HTMLElement) {
        warningEl.hidden = true;
        warningEl.textContent = '';
      }
      lastResult = null;
    };

    const showError = (message) => {
      if (resultEl instanceof HTMLElement) resultEl.hidden = true;
      if (errorEl instanceof HTMLElement) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
      lastResult = null;
    };

    const runCalculate = () => {
      hideResult();

      const methodInput = form.querySelector('[data-bra-calc-method]:checked');
      if (!(methodInput instanceof HTMLInputElement)) {
        showError('Wybierz, w jaki sposób dokonałaś pomiaru');
        return;
      }

      const underbust = parseFloat(underbustInput instanceof HTMLInputElement ? underbustInput.value : '');
      const bust = parseFloat(bustInput instanceof HTMLInputElement ? bustInput.value : '');

      if (Number.isNaN(underbust) || Number.isNaN(bust)) {
        showError('Proszę wpisać poprawne wymiary');
        return;
      }

      const result = calculateSize(underbust, bust);
      if (result.error) {
        showError('Dla tego rozmiaru nie produkuje się biustonoszy');
        return;
      }

      lastResult = { eu: result.eu, uk: result.uk };

      if (sizeEl instanceof HTMLElement) {
        sizeEl.textContent = `EU ${result.eu} / UK ${result.uk}`;
      }

      const warning = METHOD_WARNINGS[methodInput.value] || '';
      if (warningEl instanceof HTMLElement) {
        if (warning) {
          warningEl.hidden = false;
          warningEl.textContent = warning;
        } else {
          warningEl.hidden = true;
          warningEl.textContent = '';
        }
      }

      if (resultEl instanceof HTMLElement) resultEl.hidden = false;

      if (applyBtn instanceof HTMLButtonElement) {
        const select = document.querySelector(selectSelector);
        const hasOption =
          select instanceof HTMLSelectElement &&
          Array.from(select.options).some((option) => option.value === result.eu);
        applyBtn.hidden = !hasOption;
        applyBtn.disabled = !hasOption;
      }
    };

    submitBtn.addEventListener('click', runCalculate);

    form.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      if (!(event.target instanceof HTMLInputElement)) return;
      event.preventDefault();
      runCalculate();
    });

    applyBtn?.addEventListener('click', () => {
      if (!lastResult) return;
      const select = document.querySelector(selectSelector);
      if (!(select instanceof HTMLSelectElement)) return;

      const option = Array.from(select.options).find((item) => item.value === lastResult.eu);
      if (!option) return;

      select.value = lastResult.eu;
      select.dispatchEvent(new Event('change', { bubbles: true }));

      const dialogComponent = root.closest('dialog-component');
      if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
        dialogComponent.closeDialog();
      }
    });
  }

  const boot = () => {
    document.querySelectorAll('[data-bra-calculator]').forEach((root) => {
      if (root instanceof HTMLElement) initCalculator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
